import { catalogProducts, categorySeed } from '../data/catalog';
import type { Category, Product, ProductColor, ProductImage } from '../types';
import { isSupabaseConfigured, supabase } from './supabase';

type DbRow = Record<string, unknown>;

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value as T[] : [];
}

function mapProduct(row: DbRow): Product {
  const variants = asArray<DbRow>(row.product_variants);
  const colors = [...new Set(variants.map((variant) => String(variant.color || '')).filter(Boolean))]
    .map((name) => ({ name, hex: '#222222' }));
  const sizes = [...new Set(variants.map((variant) => String(variant.size || '')).filter(Boolean))];
  const images = asArray<DbRow>(row.product_images)
    .sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0))
    .map((image, index): ProductImage => ({
      id: String(image.id ?? index),
      alt: `${String(row.name)} — Sena Beachway`,
      url: String(image.image_url),
    }));
  const category = (row.categories as DbRow | null) ?? null;

  return {
    id: String(row.id),
    name: String(row.name),
    slug: String(row.slug),
    collection: String(category?.collection ?? 'fitness') as Product['collection'],
    category: String(category?.slug ?? 'conjuntos'),
    tags: [String(category?.slug ?? 'conjuntos')],
    price: row.price === null ? null : Number(row.price),
    promotionalPrice: row.promotional_price === null ? null : Number(row.promotional_price),
    description: row.description ? String(row.description) : null,
    details: row.details ? String(row.details) : null,
    composition: row.composition ? String(row.composition) : null,
    care: row.care ? String(row.care) : null,
    images,
    colors: colors.length ? colors : [{ name: 'A confirmar', hex: '#777777' }],
    sizes: sizes.length ? sizes : ['P', 'M', 'G', 'GG'],
    featured: Boolean(row.featured),
    newProduct: Boolean(row.new_product),
    bestSeller: Boolean(row.best_seller),
    active: Boolean(row.active),
    createdAt: String(row.created_at ?? new Date().toISOString()),
    provisional: row.price === null,
  };
}

export async function fetchPublishedProducts(): Promise<Product[]> {
  if (!isSupabaseConfigured || !supabase) return catalogProducts;

  const { data, error } = await supabase
    .from('products')
    .select('*, categories(name, slug, collection), product_images(id, image_url, position), product_variants(color, size)')
    .eq('active', true)
    .order('created_at', { ascending: false });

  if (error || !data?.length) return catalogProducts;
  return (data as DbRow[]).map(mapProduct);
}

export async function fetchCategories(): Promise<Category[]> {
  if (!isSupabaseConfigured || !supabase) return categorySeed;
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .eq('active', true)
    .order('name');
  if (error || !data?.length) return categorySeed;
  return (data as DbRow[]).map((category) => ({
    id: String(category.id),
    name: String(category.name),
    slug: String(category.slug),
    collection: String(category.collection) as Category['collection'],
    parentId: category.parent_id ? String(category.parent_id) : null,
    active: Boolean(category.active),
  }));
}

export type AdminProductDraft = {
  name: string;
  slug: string;
  categoryId: string;
  description?: string;
  price?: number | null;
  promotionalPrice?: number | null;
  featured: boolean;
  newProduct: boolean;
  bestSeller: boolean;
  active: boolean;
  images: string[];
  variants: Array<{ color: string; size: string; sku?: string; stock: number }>;
};

export async function saveAdminProduct(draft: AdminProductDraft) {
  if (!supabase) throw new Error('Configure o Supabase antes de cadastrar produtos.');
  const { data: product, error } = await supabase
    .from('products')
    .insert({
      name: draft.name,
      slug: draft.slug,
      category_id: draft.categoryId,
      description: draft.description || null,
      price: draft.price ?? null,
      promotional_price: draft.promotionalPrice ?? null,
      featured: draft.featured,
      new_product: draft.newProduct,
      best_seller: draft.bestSeller,
      active: draft.active,
    })
    .select('id')
    .single();
  if (error || !product) throw new Error(error?.message || 'Não foi possível criar o produto.');

  const productId = String(product.id);
  const imageRows = draft.images.filter(Boolean).map((imageUrl, position) => ({ product_id: productId, image_url: imageUrl, position }));
  if (imageRows.length) {
    const { error: imageError } = await supabase.from('product_images').insert(imageRows);
    if (imageError) throw new Error(imageError.message);
  }
  const variantRows = draft.variants.filter((variant) => variant.color && variant.size).map((variant) => ({
    product_id: productId,
    color: variant.color,
    size: variant.size,
    sku: variant.sku || null,
    stock: variant.stock,
  }));
  if (variantRows.length) {
    const { error: variantError } = await supabase.from('product_variants').insert(variantRows);
    if (variantError) throw new Error(variantError.message);
  }
}

export async function uploadProductImages(files: File[]) {
  if (!supabase) throw new Error('Configure o Supabase antes de enviar fotos.');
  const paths: string[] = [];
  for (const file of files) {
    const extension = file.name.split('.').pop() || 'jpg';
    const path = `products/${crypto.randomUUID()}.${extension}`;
    const { error } = await supabase.storage.from('product-images').upload(path, file, { upsert: false });
    if (error) throw new Error(error.message);
    paths.push(supabase.storage.from('product-images').getPublicUrl(path).data.publicUrl);
  }
  return paths;
}

export function productFromCart(productId: string, products: Product[]) {
  return products.find((product) => product.id === productId) ?? catalogProducts.find((product) => product.id === productId);
}

export function productColor(product: Product, color: string): ProductColor {
  return product.colors.find((item) => item.name === color) ?? product.colors[0];
}
